import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { API_BASE_URL } from '@/constants'
import { useAuth } from '@/hooks/use-auth'
import {
  type ClassifierRun,
  type DiscourseSessionDetail,
} from '@/hooks/use-discourse-classifiers'
import { useStreamingSse } from '@/hooks/use-streaming-sse'
import { isJwtExpired } from '@/utils/jwt'

type DiscourseLiveEnvelope = {
  domain?: string
  type?: string
  payload?: {
    turn?: {
      id: string
      discourse_session_id: string
      sequence: number
      role: string
      text: string
      turn_id: string
      source: string
    }
    run?: ClassifierRun
  }
}

export function upsertDiscourseTurn(
  session: DiscourseSessionDetail,
  turn: NonNullable<NonNullable<DiscourseLiveEnvelope['payload']>['turn']>,
): DiscourseSessionDetail {
  const existingIdx = session.turns.findIndex(
    (t) =>
      (turn.id && t.id === turn.id) ||
      (turn.turn_id && t.turn_id === turn.turn_id) ||
      t.sequence === turn.sequence,
  )
  const nextTurn = {
    id: turn.id,
    sequence: turn.sequence,
    role: turn.role,
    text: turn.text,
    turn_id: turn.turn_id,
    source: turn.source,
  }
  const turns =
    existingIdx >= 0
      ? session.turns.map((t, i) => (i === existingIdx ? nextTurn : t))
      : [...session.turns, nextTurn].sort((a, b) => a.sequence - b.sequence)
  return { ...session, turns }
}

/**
 * Subscribe to discourse session live SSE (turns + live-lens runs).
 * Patches TanStack cache — no polling.
 */
export function useDiscourseSessionSse(
  sessionId: string | undefined,
  profileId?: string | undefined,
) {
  const { token, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const onMessage = useCallback(
    (message: { data: string }) => {
      const raw = message.data?.trim()
      if (!raw) return
      let envelope: DiscourseLiveEnvelope
      try {
        envelope = JSON.parse(raw) as DiscourseLiveEnvelope
      } catch {
        return
      }

      if (envelope.domain === 'discourse' && envelope.type === 'discourse.turn.appended') {
        const turn = envelope.payload?.turn
        if (!turn?.discourse_session_id) return
        const sid = turn.discourse_session_id
        const existing = queryClient.getQueryData<DiscourseSessionDetail>([
          'discourse-session',
          sid,
        ])
        if (!existing) {
          void queryClient.invalidateQueries({ queryKey: ['discourse-session', sid] })
          return
        }
        queryClient.setQueryData<DiscourseSessionDetail>(
          ['discourse-session', sid],
          upsertDiscourseTurn(existing, turn),
        )
        return
      }

      if (envelope.domain === 'classifiers' && envelope.type === 'classifiers.run.updated') {
        const run = envelope.payload?.run
        if (!run?.discourse_session_id) return
        const sid = run.discourse_session_id
        const pid = run.profile_id || profileId || ''
        queryClient.setQueryData<ClassifierRun>(['classifier-latest', sid, pid], run)
        if (profileId && pid && profileId !== pid) {
          queryClient.setQueryData<ClassifierRun>(['classifier-latest', sid, profileId], run)
        }
      }
    },
    [profileId, queryClient],
  )

  const enabled = Boolean(isAuthenticated && sessionId && token && !isJwtExpired(token))
  const url = sessionId
    ? API_BASE_URL
      ? `${API_BASE_URL}/api/discourse/sessions/${sessionId}/live`
      : `/api/discourse/sessions/${sessionId}/live`
    : ''

  return useStreamingSse({
    url,
    token,
    enabled,
    onMessage,
    lastEventAtMinIntervalMs: 0,
  })
}
